import React, { useState, useEffect, useRef } from 'react';

/**
 * FilterDropdown - Excel-like filter dropdown with tabs for values and advanced filters
 * 
 * Features:
 * - Tabbed interface: "Select Values" and "Advanced Filter"
 * - Checkbox selection with proper additive logic (check = include)
 * - Context-aware operators based on column type (text/number/date)
 * - Search within values
 * - Select All / Clear All buttons
 * 
 * @param {Object} props
 * @param {Object} props.column - Column definition { key, label, type }
 * @param {Array} props.uniqueValues - Unique values for checkbox list
 * @param {Array} props.selectedValues - Currently selected values for this column
 * @param {Object} props.textFilter - Current text filter { mode, text }
 * @param {Object} props.numericFilter - Current numeric filter { operator, value, value2 }
 * @param {Object} props.dateFilter - Current date filter { operator, date, date2 }
 * @param {string} props.filterSearch - Current search text in dropdown
 * @param {Function} props.onToggleValue - Toggle a checkbox value
 * @param {Function} props.onSelectAll - Select all values
 * @param {Function} props.onClearColumn - Clear all filters for column
 * @param {Function} props.onApplyTextFilter - Apply text filter
 * @param {Function} props.onApplyNumericFilter - Apply numeric filter
 * @param {Function} props.onApplyDateFilter - Apply date filter
 * @param {Function} props.onClose - Close dropdown
 * @param {Function} props.onSearchChange - Search input change handler
 */
const FilterDropdown = ({
  column,
  uniqueValues = [],
  selectedValues = [],
  textFilter = null,
  numericFilter = null,
  dateFilter = null,
  filterSearch = '',
  onToggleValue,
  onSelectAll,
  onClearColumn,
  onApplyTextFilter,
  onApplyNumericFilter,
  onApplyDateFilter,
  onClose,
  onSearchChange,
}) => {
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('values'); // 'values' or 'advanced'
  
  // Local state for advanced filter inputs
  const [localTextMode, setLocalTextMode] = useState(textFilter?.mode || 'contains');
  const [localTextValue, setLocalTextValue] = useState(textFilter?.text || '');
  const [localNumericOperator, setLocalNumericOperator] = useState(numericFilter?.operator || 'eq');
  const [localNumericValue, setLocalNumericValue] = useState(numericFilter?.value ?? '');
  const [localNumericValue2, setLocalNumericValue2] = useState(numericFilter?.value2 ?? '');
  const [localDateOperator, setLocalDateOperator] = useState(dateFilter?.operator || 'eq');
  const [localDateValue, setLocalDateValue] = useState(dateFilter?.date || '');
  const [localDateValue2, setLocalDateValue2] = useState(dateFilter?.date2 || '');

  const columnType = column?.type || 'text';

  // Focus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Reset local advanced inputs whenever parent filter state changes (incl. Clear).
  useEffect(() => {
    setLocalTextMode(textFilter?.mode || 'contains');
    setLocalTextValue(textFilter?.text || '');
    setLocalNumericOperator(numericFilter?.operator || 'eq');
    setLocalNumericValue(numericFilter?.value ?? '');
    setLocalNumericValue2(numericFilter?.value2 ?? '');
    setLocalDateOperator(dateFilter?.operator || 'eq');
    setLocalDateValue(dateFilter?.date || '');
    setLocalDateValue2(dateFilter?.date2 || '');
  }, [textFilter, numericFilter, dateFilter, selectedValues]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Filter values based on search
  const filteredValues = uniqueValues.filter(val => 
    !filterSearch || val.toLowerCase().includes(filterSearch.toLowerCase())
  );

  // Text operators
  const textOperators = [
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does not contain' },
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does not equal' },
    { value: 'startsWith', label: 'Starts with' },
    { value: 'endsWith', label: 'Ends with' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notEmpty', label: 'Is not empty' },
  ];

  // Numeric operators
  const numericOperators = [
    { value: 'eq', label: 'Equals (=)' },
    { value: 'neq', label: 'Not equals (≠)' },
    { value: 'gt', label: 'Greater than (>)' },
    { value: 'gte', label: 'Greater or equal (≥)' },
    { value: 'lt', label: 'Less than (<)' },
    { value: 'lte', label: 'Less or equal (≤)' },
    { value: 'between', label: 'Between' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notEmpty', label: 'Is not empty' },
  ];

  // Date operators
  const dateOperators = [
    { value: 'eq', label: 'Equals' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
    { value: 'onOrBefore', label: 'On or before' },
    { value: 'onOrAfter', label: 'On or after' },
    { value: 'between', label: 'Between' },
    { value: 'empty', label: 'Is empty' },
    { value: 'notEmpty', label: 'Is not empty' },
  ];

  const handleApplyAdvancedFilter = () => {
    if (columnType === 'number') {
      onApplyNumericFilter(column.key, localNumericOperator, localNumericValue, localNumericValue2 || null);
    } else if (columnType === 'date') {
      onApplyDateFilter(column.key, localDateOperator, localDateValue, localDateValue2 || null);
    } else {
      onApplyTextFilter(column.key, localTextMode, localTextValue);
    }
  };

  const handleSelectAllFiltered = () => {
    // Select all filtered values (adds them to selection)
    onSelectAll(column.key, filteredValues);
  };

  const handleClearAll = () => {
    onClearColumn(column.key);
  };

  const hasActiveAdvancedFilter = textFilter || numericFilter || dateFilter;

  // Styles
  const tabStyle = (isActive) => ({
    flex: 1,
    padding: '10px 12px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: isActive ? 'white' : '#f1f3f4',
    border: 'none',
    borderBottom: isActive ? '2px solid #10b981' : '2px solid transparent',
    cursor: 'pointer',
    color: isActive ? '#10b981' : '#5f6368',
    transition: 'all 0.2s',
  });

  const buttonStyle = (variant = 'default') => {
    const base = {
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    };
    if (variant === 'primary') {
      return { ...base, backgroundColor: '#10b981', color: 'white' };
    }
    if (variant === 'success') {
      return { ...base, backgroundColor: '#34a853', color: 'white' };
    }
    if (variant === 'danger') {
      return { ...base, backgroundColor: '#065f46', color: 'white' };
    }
    return { ...base, backgroundColor: '#f1f3f4', color: '#3c4043' };
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    fontSize: '12px',
    border: '1px solid #dadce0',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    backgroundColor: 'white',
    cursor: 'pointer',
  };

  return (
    <div 
      ref={dropdownRef}
      className="filter-dropdown"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        backgroundColor: 'white',
        border: '1px solid #dadce0',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        zIndex: 1000,
        minWidth: '280px',
        maxWidth: '320px',
        maxHeight: '480px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: '#202124',
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ 
        padding: '12px 16px', 
        borderBottom: '1px solid #e8eaed',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#202124' }}>
          Filter: {column?.label || column?.key}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#5f6368',
            padding: '0 4px',
          }}
        >
          ×
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e8eaed' }}>
        <button onClick={() => setActiveTab('values')} style={tabStyle(activeTab === 'values')}>
          Select Values
        </button>
        <button onClick={() => setActiveTab('advanced')} style={tabStyle(activeTab === 'advanced')}>
          Advanced
          {hasActiveAdvancedFilter && <span style={{ marginLeft: '4px', color: '#10b981' }}>●</span>}
        </button>
      </div>

      {activeTab === 'values' ? (
        /* Values Tab */
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {/* Search */}
          <div style={{ padding: '12px', borderBottom: '1px solid #e8eaed' }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search values..."
              value={filterSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Select All / Clear All */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e8eaed', display: 'flex', gap: '8px' }}>
            <button onClick={handleSelectAllFiltered} style={buttonStyle('success')}>
              ✓ Select All ({filteredValues.length})
            </button>
            <button onClick={handleClearAll} style={buttonStyle('danger')}>
              ✕ Clear Filter
            </button>
          </div>

          {/* Status indicator */}
          {selectedValues.length > 0 && (
            <div style={{ 
              padding: '8px 12px', 
              backgroundColor: '#d1fae5', 
              borderBottom: '1px solid #e8eaed',
              fontSize: '11px',
              color: '#065f46',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{ fontWeight: '600' }}>Active:</span>
              <span>{selectedValues.length} value{selectedValues.length !== 1 ? 's' : ''} selected</span>
            </div>
          )}

          {/* Values list */}
          <div style={{ flex: 1, overflow: 'auto', padding: '4px 0', maxHeight: '200px' }}>
            {filteredValues.length === 0 ? (
              <div style={{ padding: '20px', color: '#5f6368', fontSize: '12px', textAlign: 'center' }}>
                {filterSearch ? 'No matching values' : 'No values available'}
              </div>
            ) : (
              filteredValues.map(val => {
                // Additive model: only explicitly selected values are checked.
                // After Clear, parent passes [] so every box is unchecked.
                const isChecked = selectedValues.includes(val);
                return (
                  <label
                    key={val}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      backgroundColor: isChecked ? '#d1fae5' : 'transparent',
                      transition: 'background-color 0.1s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggleValue(column.key, val)}
                      style={{ 
                        marginRight: '10px', 
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                        accentColor: '#10b981',
                      }}
                    />
                    <span style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap',
                      color: '#202124',
                    }}>
                      {val || '(empty)'}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      ) : (
        /* Advanced Tab */
        <div style={{ padding: '16px', flex: 1, overflow: 'auto' }}>
          {columnType === 'number' ? (
            /* Numeric Filters */
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Condition
                </label>
                <select
                  value={localNumericOperator}
                  onChange={(e) => setLocalNumericOperator(e.target.value)}
                  style={selectStyle}
                >
                  {numericOperators.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              </div>

              {!['empty', 'notEmpty'].includes(localNumericOperator) && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Value
                  </label>
                  <input
                    type="number"
                    value={localNumericValue}
                    onChange={(e) => setLocalNumericValue(e.target.value)}
                    placeholder="Enter number..."
                    style={inputStyle}
                  />
                </div>
              )}

              {localNumericOperator === 'between' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    And
                  </label>
                  <input
                    type="number"
                    value={localNumericValue2}
                    onChange={(e) => setLocalNumericValue2(e.target.value)}
                    placeholder="Enter second number..."
                    style={inputStyle}
                  />
                </div>
              )}
            </>
          ) : columnType === 'date' ? (
            /* Date Filters */
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Condition
                </label>
                <select
                  value={localDateOperator}
                  onChange={(e) => setLocalDateOperator(e.target.value)}
                  style={selectStyle}
                >
                  {dateOperators.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              </div>

              {!['empty', 'notEmpty'].includes(localDateOperator) && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Date
                  </label>
                  <input
                    type="date"
                    value={localDateValue}
                    onChange={(e) => setLocalDateValue(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}

              {localDateOperator === 'between' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    And
                  </label>
                  <input
                    type="date"
                    value={localDateValue2}
                    onChange={(e) => setLocalDateValue2(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              )}
            </>
          ) : (
            /* Text Filters */
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                  Condition
                </label>
                <select
                  value={localTextMode}
                  onChange={(e) => setLocalTextMode(e.target.value)}
                  style={selectStyle}
                >
                  {textOperators.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              </div>

              {!['empty', 'notEmpty'].includes(localTextMode) && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', color: '#5f6368', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                    Value
                  </label>
                  <input
                    type="text"
                    value={localTextValue}
                    onChange={(e) => setLocalTextValue(e.target.value)}
                    placeholder="Enter text..."
                    style={inputStyle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApplyAdvancedFilter();
                      }
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Apply / Clear buttons */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button onClick={handleApplyAdvancedFilter} style={{ ...buttonStyle('primary'), flex: 1 }}>
              Apply Filter
            </button>
            <button onClick={handleClearAll} style={{ ...buttonStyle(), flex: 1 }}>
              Clear
            </button>
          </div>

          {/* Active filter indicator */}
          {hasActiveAdvancedFilter && (
            <div style={{ 
              marginTop: '16px', 
              padding: '10px 12px', 
              backgroundColor: '#d1fae5', 
              borderRadius: '6px',
              fontSize: '11px',
              color: '#065f46',
            }}>
              <strong>Active filter:</strong>{' '}
              {textFilter && `${textFilter.mode} "${textFilter.text}"`}
              {numericFilter && `${numericFilter.operator} ${numericFilter.value}${numericFilter.value2 ? ` - ${numericFilter.value2}` : ''}`}
              {dateFilter && `${dateFilter.operator} ${dateFilter.date}${dateFilter.date2 ? ` to ${dateFilter.date2}` : ''}`}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        padding: '12px 16px', 
        borderTop: '1px solid #e8eaed',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: '#f8f9fa',
      }}>
        <button onClick={onClose} style={buttonStyle('primary')}>
          Done
        </button>
      </div>
    </div>
  );
};

export default FilterDropdown;
